import React, { lazy, Suspense } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { CheckCircle2, Circle, Clock, MonitorPlay, Calendar, CheckSquare, GripVertical, Pencil, Trash2, MoreHorizontal, ChevronDown, ChevronUp, Layers, FileText } from "lucide-react";
import { cn } from "../../lib/utils";
import type { Milestone } from "../../api/programs";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";

const MilestoneOutputsPanel = lazy(() => import("./MilestoneOutputsPanel"));

function InstructionViewer({ content }: { content: string }) {
  const editor = useEditor({
    extensions: [StarterKit],
    content,
    editable: false,
    editorProps: {
      attributes: { class: "prose prose-sm prose-gray max-w-none focus:outline-none" },
    },
  });
  if (!editor) return null;
  return <EditorContent editor={editor} />;
}

interface MilestoneRowProps {
  milestone: Milestone;
  programId?: string;
  /** If true, renders in read-only mode (client view or completed view) */
  readonly?: boolean;
  /** Show drag handle (editor mode only) */
  draggable?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  onEdit?: (milestone: Milestone) => void;
  onDelete?: (milestoneId: string) => void;
  onComplete?: (milestoneId: string) => void;
  /** Client action: open the linked entity */
  onOpen?: (milestone: Milestone) => void;
  /** Client action: opt into an optional pending milestone */
  onActivateOptional?: (milestoneId: string) => void;
  /** Expand/collapse outputs panel */
  isExpanded?: boolean;
  onExpand?: (milestoneId: string) => void;
  /** True when the current user is the program's mentor */
  isMentor?: boolean;
}

const TYPE_ICONS: Record<Milestone["type"], React.ReactNode> = {
  activity: <MonitorPlay className="w-4 h-4" />,
  meeting: <Calendar className="w-4 h-4" />,
  action_item: <CheckSquare className="w-4 h-4" />,
  identity_space: <Layers className="w-4 h-4" />,
  instruction: <FileText className="w-4 h-4" />,
};

function StatusIcon({ status }: { status: Milestone["status"] }) {
  if (status === "completed") return <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />;
  if (status === "in_progress") return <Clock className="w-5 h-5 text-primary shrink-0" />;
  return <Circle className="w-5 h-5 text-gray-300 shrink-0" />;
}

const MilestoneRow: React.FC<MilestoneRowProps> = ({
  milestone,
  programId,
  readonly = false,
  draggable = false,
  dragHandleProps,
  onEdit,
  onDelete,
  onComplete,
  onOpen,
  onActivateOptional,
  isExpanded = false,
  onExpand,
  isMentor = false,
}) => {
  const { t } = useTranslation();
  const isCompleted = milestone.status === "completed";
  const isActive = milestone.status === "in_progress";
  const hasExpectedOutputs = (milestone.expectedOutputs?.length ?? 0) > 0;
  const canExpand =
    !!programId &&
    (hasExpectedOutputs || (isCompleted && !!milestone.linkedEntityId && milestone.type !== "instruction"));

  return (
    <div
      className={cn(
        "rounded-lg border transition-colors",
        isCompleted && "bg-gray-50 border-gray-200",
        isActive && "bg-primary/5/50 border-primary/20",
        !isCompleted && !isActive && "bg-white border-gray-200",
        readonly && isActive && "bg-primary/5/50 border-primary/20"
      )}
    >
    <div className={cn("flex items-center gap-3 px-3 py-3", isCompleted && "opacity-70")}>
      {/* Drag handle — editor only */}
      {draggable && (
        <div
          {...dragHandleProps}
          className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-400 shrink-0"
        >
          <GripVertical className="w-4 h-4" />
        </div>
      )}

      {/* Status icon */}
      <StatusIcon status={milestone.status} />

      {/* Type icon + content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn(
            "text-sm font-medium truncate",
            isCompleted ? "line-through text-muted-foreground" : "text-foreground"
          )}>
            {milestone.title}
          </span>
          {milestone.isOptional && (
            <span className="text-[10px] text-muted-foreground border border-gray-200 rounded px-1 py-0.5 shrink-0">
              {t("programs.milestones.optional")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={cn("text-muted-foreground/70", isActive ? "text-primary/70" : "")}>
            {TYPE_ICONS[milestone.type]}
          </span>
          <span className="text-xs text-muted-foreground">
            {t(`programs.milestones.type.${milestone.type}`)}
          </span>
          {milestone.linkedEntityName && (
            <>
              <span className="text-xs text-muted-foreground/40">·</span>
              <span className="text-xs text-muted-foreground truncate">{milestone.linkedEntityName}</span>
            </>
          )}
          {(milestone.dueDate != null || milestone.dueDateOffset != null) && (
            <>
              <span className="text-xs text-muted-foreground/40">·</span>
              <span className="text-[10px] font-medium text-violet-500 border border-violet-200 rounded px-1 py-0.5 shrink-0">
                {milestone.dueDate != null
                  ? new Date(milestone.dueDate).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                  : t("programs.milestones.dayX", { day: milestone.dueDateOffset })}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        {/* Expand toggle — completed milestones with a linked entity */}
        {canExpand && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onExpand?.(milestone.id)}
          >
            {isExpanded
              ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
              : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
            }
          </Button>
        )}

        {readonly ? (
          // Optional pending — show an Activate button so the client can opt in
          milestone.isOptional && milestone.status === "pending" ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onActivateOptional?.(milestone.id)}
              className="h-7 text-xs"
            >
              {t("programs.milestones.activateOptional")}
            </Button>
          ) : isActive ? (
            <>
              {/* Instruction steps and unlinked identity_space steps have nothing to "open" */}
              {onOpen && milestone.type !== "instruction" && milestone.linkedEntityId && (
                <Button size="sm" onClick={() => onOpen(milestone)} className="h-7 text-xs bg-primary hover:bg-primary/90">
                  {t("common.open")}
                </Button>
              )}
              {onComplete && (
                <Button size="sm" variant="outline" onClick={() => onComplete(milestone.id)} className="h-7 text-xs">
                  {t("programs.milestones.markDone")}
                </Button>
              )}
            </>
          ) : milestone.status === "pending" ? (
            // Non-optional pending — waiting for a prior step to complete
            <span className="text-[11px] text-muted-foreground/60 italic">
              {t("programs.milestones.waitingForPrior")}
            </span>
          ) : null
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(milestone)}>
                  <Pencil className="w-4 h-4 me-2" /> {t("common.edit")}
                </DropdownMenuItem>
              )}
              {onComplete && !isCompleted && (
                <DropdownMenuItem onClick={() => onComplete(milestone.id)}>
                  <CheckCircle2 className="w-4 h-4 me-2 text-emerald-500" /> {t("programs.milestones.markComplete")}
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem onClick={() => onDelete(milestone.id)} className="text-destructive focus:text-destructive">
                  <Trash2 className="w-4 h-4 me-2" /> {t("common.remove")}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>{/* end inner row */}

    {/* Instruction content — always visible for instruction steps in client view */}
    {readonly && milestone.type === "instruction" && milestone.notes && (
      <div className="px-4 pb-3 pt-0">
        <InstructionViewer content={milestone.notes} />
      </div>
    )}

    {/* Optional pending hint — shown below the row for client */}
    {readonly && milestone.isOptional && milestone.status === "pending" && (
      <div className="px-4 pb-3 pt-0">
        <p className="text-xs text-muted-foreground italic">{t("programs.milestones.optionalHint")}</p>
      </div>
    )}

    {/* Outputs panel — shown when expanded */}
    {canExpand && isExpanded && (
      <Suspense fallback={
        <div className="px-3 pb-3 space-y-1.5">
          <div className="h-3 w-3/4 rounded bg-gray-100 animate-pulse" />
          <div className="h-3 w-1/2 rounded bg-gray-100 animate-pulse" />
        </div>
      }>
        <MilestoneOutputsPanel programId={programId!} milestoneId={milestone.id} milestone={milestone} isMentor={isMentor} />
      </Suspense>
    )}
    </div>
  );
};

export default MilestoneRow;
